import React, {useMemo} from 'react';

import {useProjectParams} from '../../pages/ProjectPage/useProjectParams';
import {PanelSearchContextProvider} from '../../state/panelbank/PanelSearchContextProvider';
import {
  EntityProjectPermissions,
  useViewerPermissionsForEntityProject,
} from '../../util/entityProjectPermissions';
import {AutoSyncPanelBankContextProvider} from '../PanelBank/contextProviders/AutoSyncPanelBankContext';
import {DefinedMetricContextProvider} from '../PanelBank/contextProviders/DefinedMetricContext';
import {PanelBankSectionsScrollContextProvider} from '../PanelBank/contextProviders/PanelBankSectionsScrollContextProvider';
import {PanelBankWidthContextProvider} from '../PanelBank/contextProviders/PanelBankWidthContext';
import {WandbConfigContextProvider} from '../PanelBank/contextProviders/WandbConfigContext';
import RunSelector from '../RunSelector';
import {readOnlyRunSelectorDisabledFeatures} from '../RunSelector/util';
import {PanelsQuickAddContextProvider} from '../WorkspaceDrawer/PanelsQuickAdd/PanelsQuickAddContext';
import {WorkspaceDrawer} from '../WorkspaceDrawer/WorkspaceDrawer';
import {RunSelectorResizingContextProvider} from './RunSelectorResizingContext';
import {type MultiRunWorkspaceAllProps} from './types';

type WorkspaceWithRunSelectorProps = {
  children?: React.ReactNode;
  isProjectReadOnly: boolean;
  isSingleMode: boolean;
  isTableExpanded: boolean;
  toggleMinimizeRunSelectorWithTracking: (method: 'button' | 'hotkey') => void;
  onSelectRunName: MultiRunWorkspaceAllProps['onSelectRunName'];
  onSetTableExpanded: MultiRunWorkspaceAllProps['onSetTableExpanded'];
  panelBankWidth: number;
  recommendedGrouping: MultiRunWorkspaceAllProps['recommendedGrouping'];
  runSetRef: MultiRunWorkspaceAllProps['runSetRef'];
  sectionPart: MultiRunWorkspaceAllProps['sectionPart'];
  selectedRunName: string | undefined;
  setPanelBankWidth: (width: number) => void;
  shouldShowRunSelector: boolean;
  showArtifactCounts: boolean;
  showLogCounts: boolean;
  subTitle?: string;
  title: string;
  toggleExpandRunsTableWithTracking: (method: 'button' | 'hotkey') => void;
};

export const WorkspaceWithRunSelector = ({
  children,
  isProjectReadOnly,
  isSingleMode,
  isTableExpanded,
  toggleMinimizeRunSelectorWithTracking,
  onSelectRunName,
  onSetTableExpanded,
  panelBankWidth,
  recommendedGrouping,
  runSetRef,
  sectionPart,
  selectedRunName,
  setPanelBankWidth,
  shouldShowRunSelector,
  showArtifactCounts,
  showLogCounts,
  subTitle,
  title,
  toggleExpandRunsTableWithTracking,
}: WorkspaceWithRunSelectorProps) => {
  const {entityName, projectName} = useProjectParams();
  // When the project is read-only, run-action buttons are normally all hidden.
  // But a custom role inheriting from Viewer can be explicitly granted individual
  // run:* permissions, so we keep each run action enabled when the viewer's role
  // grants the matching permission. Non-run actions (tag, create-sweep,
  // pin-cross-project-runs) have no run:* equivalent and stay disabled.
  // Mutation-level auth still enforces the real permission on the server.
  //
  // Fetch the viewer's permission set once (skipped entirely while the project is
  // writable, since there is nothing to gate) and check each run:* permission in
  // memory, rather than firing one query per permission.
  const {getHasPermission} = useViewerPermissionsForEntityProject(
    projectName,
    entityName,
    !isProjectReadOnly
  );
  const disabledFeatures = useMemo(
    () =>
      isProjectReadOnly
        ? readOnlyRunSelectorDisabledFeatures({
            canMoveRun: getHasPermission(EntityProjectPermissions.MoveRun),
            canDeleteRun: getHasPermission(EntityProjectPermissions.DeleteRun),
            canUpdateRun: getHasPermission(EntityProjectPermissions.UpdateRun),
            canStopRun: getHasPermission(EntityProjectPermissions.StopRun),
          })
        : undefined,
    [isProjectReadOnly, getHasPermission]
  );

  return (
    <RunSelectorResizingContextProvider>
      {shouldShowRunSelector && (
        <RunSelector
          analyticsLocation={isTableExpanded ? 'runs-table' : 'workspace'}
          className="single-wb-table"
          customRunColorsRef={sectionPart.customRunColorsRef}
          customRunNamesRef={sectionPart.customRunNamesRef}
          disabledFeatures={disabledFeatures}
          isExpandable
          isExpanded={isTableExpanded}
          isSingleMode={isSingleMode}
          toggleMinimizeRunSelectorWithTracking={
            toggleMinimizeRunSelectorWithTracking
          }
          // Make sure to remount if the runSetRef id changes (happens when we load a new workspace, or clear our workspace)
          key={runSetRef.id}
          // Unfortunately, `RunSelector` has a `readOnly` prop that means something completely different
          // (it doesn't let the user set filters, grouping, etc.).
          // Instead, this `readOnly` variable refers to the current viewer's access to the project
          // (whether or not they can rename/delete runs, etc.), so we pass it in to `RunSelector` as the `noEdit` prop instead.
          onSelectRunName={onSelectRunName}
          onSetExpanded={onSetTableExpanded}
          recommendedGrouping={recommendedGrouping}
          runSetRef={runSetRef}
          selectedRunName={selectedRunName}
          semanticLegendRef={sectionPart.semanticLegendRef}
          showArtifactCounts={showArtifactCounts}
          showLogCounts={showLogCounts}
          subTitle={subTitle}
          title={title}
          toggleExpandRunsTableWithTracking={toggleExpandRunsTableWithTracking}
        />
      )}
      <PanelsQuickAddContextProvider>
        <PanelBankWidthContextProvider
          panelBankWidth={panelBankWidth}
          setPanelBankWidth={setPanelBankWidth}>
          <WandbConfigContextProvider>
            <DefinedMetricContextProvider>
              <PanelSearchContextProvider>
                <AutoSyncPanelBankContextProvider>
                  <PanelBankSectionsScrollContextProvider>
                    {children}
                    <WorkspaceDrawer />
                  </PanelBankSectionsScrollContextProvider>
                </AutoSyncPanelBankContextProvider>
              </PanelSearchContextProvider>
            </DefinedMetricContextProvider>
          </WandbConfigContextProvider>
        </PanelBankWidthContextProvider>
      </PanelsQuickAddContextProvider>
    </RunSelectorResizingContextProvider>
  );
};
